import { z } from "zod";
import { BORDER_PASS_RULES } from "./delivery";

/**
 * Настройки системы, которые администратор меняет во вкладке «Настройки» (раздел 16.4).
 * Хранятся в таблице settings (key/value jsonb); значения по умолчанию — в apps/api/src/services/settings.ts.
 * Новые значения действуют на лоты, публикуемые после изменения: шаг снимается в лот при создании.
 */
export const DELIVERY_NOTIFY_SCOPES = ["all", "author"] as const;
export type DeliveryNotifyScope = (typeof DELIVERY_NOTIFY_SCOPES)[number];

export const appSettingsSchema = z.object({
  /** Минимальный шаг понижения, тенге. */
  min_step: z.number().int().min(1).max(100_000_000),
  /** Автопродление: на сколько минут продлевать и максимум раз за лот. */
  auto_extend_minutes: z.number().int().min(1).max(1440),
  auto_extend_max: z.number().int().min(0).max(20),
  /** Когда талон границы обязателен для подтверждения доставки (14.1). */
  border_pass_rule: z.enum(BORDER_PASS_RULES),
  /** Кого уведомлять о доставке: всех логистов или только автора лота (14.1). */
  delivery_notify_scope: z.enum(DELIVERY_NOTIFY_SCOPES),
  /** Срок подачи на оплату: подтверждение доставки + N календарных дней (14.2). */
  payment_due_days: z.number().int().min(1).max(365),
  /** За сколько дней до срока оплаты напоминать логисту (14.3.5). */
  payment_reminder_days: z.number().int().min(1).max(60),
});
export type AppSettings = z.infer<typeof appSettingsSchema>;
export type AppSettingKey = keyof AppSettings;
export const APP_SETTING_KEYS = Object.keys(appSettingsSchema.shape) as AppSettingKey[];

export const APP_SETTING_LABELS: Record<AppSettingKey, string> = {
  min_step: "Шаг понижения, ₸",
  auto_extend_minutes: "Автопродление, минут",
  auto_extend_max: "Автопродление, максимум раз",
  border_pass_rule: "Талон прохождения границы",
  delivery_notify_scope: "Кого уведомлять о доставке",
  payment_due_days: "Срок оплаты, дней",
  payment_reminder_days: "Напоминание об оплате за N дней",
};

export const BORDER_PASS_RULE_LABELS: Record<(typeof BORDER_PASS_RULES)[number], string> = {
  international: "Только для международных перевозок",
  always: "Всегда",
  never: "Никогда",
};

export const DELIVERY_NOTIFY_SCOPE_LABELS: Record<DeliveryNotifyScope, string> = {
  all: "Всех логистов",
  author: "Только автора заявки",
};

/**
 * Подмножество настроек, нужное формам логиста (подписи «автопродление 30 мин, не более 2 раз», срок оплаты).
 * GET /api/dictionaries/settings — доступно всем авторизованным.
 */
export type PublicSettings = Pick<AppSettings, "min_step" | "auto_extend_minutes" | "auto_extend_max" | "payment_due_days">;
